import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Building2, Users as UsersIcon, FileText, Coins, BadgeCheck, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { useUXChrome } from "../contexts/UXChromeContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const TYPE_LABEL = {
    employee: "Employees",
    contract: "Contracts",
    payroll_period: "Payroll periods",
    wage_rate: "Wage rates",
    module: "Modules",
    user: "Users",
};
const ICONS = {
    employee: UsersIcon,
    contract: FileText,
    payroll_period: Coins,
    wage_rate: BadgeCheck,
    module: Building2,
    user: UsersIcon,
};

const RECENT_KEY = "asl_cmdk_recents";
const HINT_KEY = "asl_cmdk_hint_shown";

export default function CommandPalette() {
    const { commandOpen, setCommandOpen } = useUXChrome();
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [active, setActive] = useState(0);
    const [recents, setRecents] = useState([]);
    const inputRef = useRef(null);
    const debTimer = useRef(null);

    useEffect(() => {
        if (commandOpen) {
            setQ("");
            setResults([]);
            setActive(0);
            try {
                const r = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
                if (Array.isArray(r)) setRecents(r.slice(0, 5));
            } catch { /* noop */ }
            // Increment visit hint counter
            const n = Number(localStorage.getItem(HINT_KEY) || "0") + 1;
            localStorage.setItem(HINT_KEY, String(n));
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [commandOpen]);

    useEffect(() => {
        clearTimeout(debTimer.current);
        if (!commandOpen) return;
        if (!q.trim()) {
            setResults([]);
            setActive(0);
            return;
        }
        debTimer.current = setTimeout(async () => {
            try {
                const { data } = await api.get(`/search`, { params: { q: q.trim() } });
                setResults(data.results || []);
                setActive(0);
            } catch (e) {
                toast.error(formatApiError(e));
            }
        }, 180);
        return () => clearTimeout(debTimer.current);
    }, [q, commandOpen]);

    const grouped = useMemo(() => {
        const out = {};
        results.forEach((r) => {
            (out[r.type] = out[r.type] || []).push(r);
        });
        return out;
    }, [results]);

    const flat = useMemo(() => {
        const order = ["module", "employee", "contract", "payroll_period", "wage_rate", "user"];
        const out = [];
        order.forEach((t) => (grouped[t] || []).forEach((r) => out.push(r)));
        return out;
    }, [grouped]);

    const choose = (item) => {
        if (!item) return;
        const recents = (() => {
            try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
        })();
        const next = [item, ...recents.filter((r) => !(r.id === item.id && r.type === item.type))].slice(0, 5);
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
        setCommandOpen(false);
        if (item.route) navigate(item.route);
    };

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(flat.length - 1, a + 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
        else if (e.key === "Enter") { e.preventDefault(); choose(flat[active]); }
    };

    const showHint = (Number(localStorage.getItem(HINT_KEY) || "0") <= 3);
    const list = q.trim() ? flat : recents;
    const renderRow = (item, idx) => {
        const Icon = ICONS[item.type] || Search;
        const selected = idx === active;
        return (
            <button
                key={`${item.type}-${item.id}`}
                onClick={() => choose(item)}
                onMouseEnter={() => setActive(idx)}
                data-testid={`cmdk-result-${item.type}-${item.id}`}
                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 ${selected ? "bg-[color:var(--asl-navy-50)]" : "hover:bg-gray-50"}`}
            >
                <Icon className="h-4 w-4 asl-navy-text shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="asl-navy-text font-medium truncate">{item.label}</div>
                    <div className="text-[11px] text-[color:var(--asl-muted)] mono truncate">{item.sub_label}</div>
                </div>
                {item.module_code && (
                    <span className="text-[10px] uppercase asl-crimson-text mono">{item.module_code}</span>
                )}
                <ArrowRight className="h-3.5 w-3.5 text-[color:var(--asl-muted)] shrink-0" />
            </button>
        );
    };

    return (
        <Dialog open={commandOpen} onOpenChange={setCommandOpen}>
            <DialogContent className="max-w-xl p-0 gap-0" data-testid="command-palette">
                <DialogTitle className="sr-only">Quick search</DialogTitle>
                <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: "var(--asl-border)" }}>
                    <Search className="h-4 w-4 text-[color:var(--asl-muted)]" />
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="Search employees, contracts, periods, rates…"
                        className="flex-1 outline-none text-sm bg-transparent"
                        data-testid="cmdk-input"
                    />
                    <span className="text-[10px] text-[color:var(--asl-muted)] mono">Esc</span>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                    {!q.trim() && recents.length > 0 && (
                        <div className="px-4 py-2 text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)]">Recent</div>
                    )}
                    {!q.trim() && recents.length === 0 && (
                        <div className="px-4 py-8 text-center text-xs text-[color:var(--asl-muted)]" data-testid="cmdk-placeholder">
                            {showHint
                                ? "Type to search across employees, contracts, payroll periods, wage rates, modules and users.\nPress ⌘K / Ctrl+K to open this anytime."
                                : "Type to search…"}
                        </div>
                    )}
                    {!q.trim() && recents.length > 0 && (
                        <div className="pb-2">{recents.map(renderRow)}</div>
                    )}
                    {q.trim() && flat.length === 0 && (
                        <div className="px-4 py-8 text-center text-xs text-[color:var(--asl-muted)]" data-testid="cmdk-noresults">
                            No matches for &ldquo;{q}&rdquo;.
                        </div>
                    )}
                    {q.trim() && Object.entries(grouped).map(([type, list]) => (
                        <div key={type} className="pb-1">
                            <div className="px-4 py-1.5 text-[10px] uppercase tracking-wider text-[color:var(--asl-muted)] flex items-center gap-1">
                                <ChevronRight className="h-3 w-3" /> {TYPE_LABEL[type] || type}
                            </div>
                            {list.map((item) => {
                                const idx = flat.indexOf(item);
                                return renderRow(item, idx);
                            })}
                        </div>
                    ))}
                </div>
                <div className="px-4 py-2 border-t flex items-center justify-between text-[10px] text-[color:var(--asl-muted)]" style={{ borderColor: "var(--asl-border)" }}>
                    <span>↑↓ to navigate · ↵ to open</span>
                    <span>Press <kbd className="mono px-1 py-0.5 rounded bg-gray-100">?</kbd> for shortcuts</span>
                </div>
            </DialogContent>
        </Dialog>
    );
}
